import { useMemo } from 'react';
import { useCommissionData } from '../contexts/CommissionDataContext';
import { periods } from '../data/commissions';
import { officers } from '../data/officers';
import { BpsAdjustment, Period, RoleGroup } from '../types/commission';
import { OfficerPeriodResult, computeOfficerPeriod } from '../utils/officerPeriod';

export interface CompanySummary {
  commission: number;
  /** Personal funded loans from production roles — avoids double-counting team units. */
  fundedUnits: number;
  fundedVolume: number;
  paidCount: number;
}

export interface TrendPoint {
  label: string;
  periodLabel: string;
  commission: number;
  fundedUnits: number;
  isSelected: boolean;
}

export interface GroupBreakdown {
  group: RoleGroup;
  commission: number;
  headcount: number;
  share: number;
}

const groupOrder: RoleGroup[] = ['Production', 'Leadership', 'Operations'];

function resultsFor(
  period: Period,
  adjustments: Record<string, BpsAdjustment>,
  overrides: Record<string, BpsAdjustment>,
): OfficerPeriodResult[] {
  return officers.map((officer) => computeOfficerPeriod(officer, period, adjustments, overrides));
}

function summarize(results: OfficerPeriodResult[]): CompanySummary {
  const production = results.filter((result) => result.role.group === 'Production');
  return {
    commission: results.reduce((sum, result) => sum + result.totals.net, 0),
    fundedUnits: production.reduce((sum, result) => sum + result.units, 0),
    fundedVolume: production.reduce((sum, result) => sum + result.volume, 0),
    paidCount: results.filter((result) => result.totals.net > 0).length,
  };
}

/** Company-wide commission and funded-unit totals for the admin overview. */
export function useCompanyOverview() {
  const { periodId, adjustments, overrides } = useCommissionData();
  const periodIndex = Math.max(
    0,
    periods.findIndex((item) => item.id === periodId),
  );
  const period = periods[periodIndex];
  const prior = periods[periodIndex + 1] ?? null;

  return useMemo(() => {
    const rows = resultsFor(period, adjustments, overrides);
    const current = summarize(rows);
    const priorSummary = prior ? summarize(resultsFor(prior, adjustments, overrides)) : null;

    const trend: TrendPoint[] = [...periods].reverse().map((item) => {
      const summary = item.id === period.id ? current : summarize(resultsFor(item, adjustments, overrides));
      return {
        label: item.label.slice(0, 3),
        periodLabel: item.label,
        commission: Math.round(summary.commission),
        fundedUnits: summary.fundedUnits,
        isSelected: item.id === period.id,
      };
    });

    const groups: GroupBreakdown[] = groupOrder.map((group) => {
      const groupRows = rows.filter((row) => row.role.group === group);
      const commission = groupRows.reduce((sum, row) => sum + row.totals.net, 0);
      return {
        group,
        commission,
        headcount: groupRows.length,
        share: current.commission > 0 ? commission / current.commission : 0,
      };
    });

    return { period, prior, rows, current, priorSummary, trend, groups };
  }, [period, prior, adjustments, overrides]);
}
